/**
 * Feedback Store
 *
 * Manages user feedback state including message-level and session-level feedback.
 * Persists to localStorage with pending sync queue for backend submission.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { MessageFeedbackData } from '@/components/feedback'
import type { SessionFeedbackData, FeedbackCategory } from '@/components/feedback'

// Aggregate feedback stats
export interface FeedbackStats {
  totalMessages: number
  positiveCount: number
  negativeCount: number
  averageSessionRating: number
  totalSessions: number
  categoryBreakdown: Record<FeedbackCategory, number>
}

// Feedback item with sync status
interface SyncableFeedback<T> {
  data: T
  synced: boolean
  syncAttempts: number
  lastSyncAttempt?: number
}

interface FeedbackState {
  // Message feedback
  messageFeedback: Record<string, SyncableFeedback<MessageFeedbackData>>

  // Session feedback
  sessionFeedback: Record<string, SyncableFeedback<SessionFeedbackData>>

  // Pending sync queue
  pendingSyncCount: number

  // Stats
  stats: FeedbackStats

  // Actions
  addMessageFeedback: (feedback: MessageFeedbackData) => void
  addSessionFeedback: (feedback: SessionFeedbackData) => void
  getMessageFeedback: (messageId: string) => MessageFeedbackData | null
  getSessionFeedback: (sessionId: string) => SessionFeedbackData | null
  markSynced: (type: 'message' | 'session', id: string) => void
  getPendingFeedback: () => {
    messages: MessageFeedbackData[]
    sessions: SessionFeedbackData[]
  }
  clearAll: () => void
  recalculateStats: () => void
}

const initialStats: FeedbackStats = {
  totalMessages: 0,
  positiveCount: 0,
  negativeCount: 0,
  averageSessionRating: 0,
  totalSessions: 0,
  categoryBreakdown: {
    accuracy: 0,
    speed: 0,
    helpfulness: 0,
    naturalness: 0,
    relevance: 0,
    other: 0,
  },
}

export const useFeedbackStore = create<FeedbackState>()(
  persist(
    (set, get) => ({
      messageFeedback: {},
      sessionFeedback: {},
      pendingSyncCount: 0,
      stats: initialStats,

      addMessageFeedback: (feedback) => {
        set((state) => {
          const newMessageFeedback = {
            ...state.messageFeedback,
            [feedback.messageId]: {
              data: feedback,
              synced: false,
              syncAttempts: 0,
            },
          }

          // Update stats
          const oldFeedback = state.messageFeedback[feedback.messageId]?.data
          let { positiveCount, negativeCount, totalMessages } = state.stats

          // Remove old feedback from stats if exists
          if (oldFeedback) {
            if (oldFeedback.rating === 'positive') positiveCount--
            if (oldFeedback.rating === 'negative') negativeCount--
          } else {
            totalMessages++
          }

          // Add new feedback to stats
          if (feedback.rating === 'positive') positiveCount++
          if (feedback.rating === 'negative') negativeCount++

          const pendingSyncCount = Object.values(newMessageFeedback).filter(
            (f) => !f.synced
          ).length + Object.values(state.sessionFeedback).filter(
            (f) => !f.synced
          ).length

          return {
            messageFeedback: newMessageFeedback,
            pendingSyncCount,
            stats: {
              ...state.stats,
              totalMessages,
              positiveCount,
              negativeCount,
            },
          }
        })
      },

      addSessionFeedback: (feedback) => {
        set((state) => {
          const newSessionFeedback = {
            ...state.sessionFeedback,
            [feedback.sessionId]: {
              data: feedback,
              synced: false,
              syncAttempts: 0,
            },
          }

          // Update stats
          const sessions = Object.values(newSessionFeedback).map((f) => f.data)
          const totalSessions = sessions.length
          const averageSessionRating =
            sessions.reduce((sum, s) => sum + s.rating, 0) / totalSessions

          // Category breakdown
          const categoryBreakdown = { ...initialStats.categoryBreakdown }
          sessions.forEach((session) => {
            session.categories.forEach((category) => {
              categoryBreakdown[category]++
            })
          })

          const pendingSyncCount = Object.values(state.messageFeedback).filter(
            (f) => !f.synced
          ).length + Object.values(newSessionFeedback).filter(
            (f) => !f.synced
          ).length

          return {
            sessionFeedback: newSessionFeedback,
            pendingSyncCount,
            stats: {
              ...state.stats,
              totalSessions,
              averageSessionRating,
              categoryBreakdown,
            },
          }
        })
      },

      getMessageFeedback: (messageId) => {
        const feedback = get().messageFeedback[messageId]
        return feedback?.data ?? null
      },

      getSessionFeedback: (sessionId) => {
        const feedback = get().sessionFeedback[sessionId]
        return feedback?.data ?? null
      },

      markSynced: (type, id) => {
        set((state) => {
          if (type === 'message') {
            const feedback = state.messageFeedback[id]
            if (!feedback) return state

            return {
              messageFeedback: {
                ...state.messageFeedback,
                [id]: { ...feedback, synced: true },
              },
              pendingSyncCount: state.pendingSyncCount - 1,
            }
          } else {
            const feedback = state.sessionFeedback[id]
            if (!feedback) return state

            return {
              sessionFeedback: {
                ...state.sessionFeedback,
                [id]: { ...feedback, synced: true },
              },
              pendingSyncCount: state.pendingSyncCount - 1,
            }
          }
        })
      },

      getPendingFeedback: () => {
        const state = get()
        return {
          messages: Object.values(state.messageFeedback)
            .filter((f) => !f.synced)
            .map((f) => f.data),
          sessions: Object.values(state.sessionFeedback)
            .filter((f) => !f.synced)
            .map((f) => f.data),
        }
      },

      clearAll: () => {
        set({
          messageFeedback: {},
          sessionFeedback: {},
          pendingSyncCount: 0,
          stats: initialStats,
        })
      },

      recalculateStats: () => {
        const state = get()

        // Message stats
        const messages = Object.values(state.messageFeedback).map((f) => f.data)
        const positiveCount = messages.filter(
          (m) => m.rating === 'positive'
        ).length
        const negativeCount = messages.filter(
          (m) => m.rating === 'negative'
        ).length

        // Session stats
        const sessions = Object.values(state.sessionFeedback).map((f) => f.data)
        const totalSessions = sessions.length
        const averageSessionRating =
          totalSessions > 0
            ? sessions.reduce((sum, s) => sum + s.rating, 0) / totalSessions
            : 0

        const categoryBreakdown = { ...initialStats.categoryBreakdown }
        sessions.forEach((session) => {
          session.categories.forEach((category) => {
            categoryBreakdown[category]++
          })
        })

        set({
          stats: {
            totalMessages: messages.length,
            positiveCount,
            negativeCount,
            averageSessionRating,
            totalSessions,
            categoryBreakdown,
          },
        })
      },
    }),
    {
      name: 'ai-expert-feedback',
      version: 1,
    }
  )
)
